'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import VerifiedBadge from '@/components/VerifiedBadge';
import AdBanner from '@/components/AdBanner';

// ─── Data ────────────────────────────────────────────────────────────────────

const verificationSteps = [
  {
    id: 'step-1',
    step: '01',
    icon: 'UserCircleIcon',
    title: 'Create Your Account',
    description:
      'Register for free in under two minutes. Choose any membership tier — Free, Pro, or Booster. Verification is available to every member, not just paid subscribers.',
  },
  {
    id: 'step-2',
    step: '02',
    icon: 'IdentificationIcon',
    title: 'Submit Your Documents',
    description:
      'Upload a valid photo ID (passport or driving licence) and, if you have one, your farm assurance certificate (e.g. Red Tractor, Quality Meat Scotland). All documents are encrypted and handled securely.',
  },
  {
    id: 'step-3',
    step: '03',
    icon: 'MagnifyingGlassIcon',
    title: 'Our Team Reviews',
    description:
      'Our UK-based verification team validates your ID, confirms your business details, and checks your farm assurance status where you have provided a certificate. This typically takes 2–3 business days.',
  },
  {
    id: 'step-4',
    step: '04',
    icon: 'CheckBadgeIcon',
    title: 'Badge Applied to Your Profile',
    description:
      'Once approved, your Verified badge appears on your profile and all your listings immediately. Buyers can see at a glance that you are a legitimate, traceable farmer or dealer.',
  },
];

const documentsRequired = [
  {
    id: 'doc-business',
    icon: 'DocumentTextIcon',
    title: 'Farm or Business Details',
    description:
      'Your farm or trading name, address and county, plus your Companies House number if you trade as a limited company. Used to confirm you are a genuine agricultural business.',
    required: true,
  },
  {
    id: 'doc-id',
    icon: 'IdentificationIcon',
    title: 'Photo ID',
    description:
      'A valid UK passport, driving licence, or national identity card. Must be current and clearly legible. We verify that the name on your ID matches the account holder.',
    required: true,
  },
  {
    id: 'doc-assurance',
    icon: 'ShieldCheckIcon',
    title: 'Farm Assurance Certificate',
    description:
      'Red Tractor, Quality Meat Scotland, RSPCA Assured, Organic Certification, or equivalent. Demonstrates your commitment to recognised farming standards.',
    required: false,
  },
  {
    id: 'doc-business',
    icon: 'BuildingOffice2Icon',
    title: 'Business Registration (Dealers)',
    description:
      'If you are a livestock dealer or agricultural merchant rather than a primary producer, provide your Companies House registration number or sole trader documentation.',
    required: false,
  },
];

const membershipTiers = [
  {
    id: 'tier-free',
    name: 'Free',
    icon: 'UserIcon',
    colour: 'border-border bg-card',
    iconBg: 'bg-muted',
    iconColour: 'text-muted-foreground',
    badgeType: 'standard' as const,
    headline: 'Verified from day one',
    description:
      'Even on our free tier, every member can apply for verification. There are no paywalls on trust. Free members who complete verification receive the standard Verified badge on their profile and listings.',
    perks: [
      'Standard Verified badge on profile',
      'Verified badge shown on all listings',
      'Farm or business details confirmed',
      'Photo ID checked against the account holder',
      'Trusted by buyers across the UK',
    ],
  },
  {
    id: 'tier-pro',
    name: 'Pro',
    icon: 'StarIcon',
    colour: 'border-primary ring-2 ring-primary/20 bg-card',
    iconBg: 'bg-primary/10',
    iconColour: 'text-primary',
    badgeType: 'standard' as const,
    headline: 'Verified with full trading power',
    description:
      'Pro members carry the same Verified badge with the added credibility of an active subscription. Buyers know Pro members are serious traders with unlimited listings and instant access to new stock.',
    perks: [
      'Standard Verified badge on profile',
      'Verified badge on unlimited listings',
      'ID & business verification included',
      'Farm assurance status displayed',
      'Priority in buyer search results',
      'Verified status on haulage job posts',
    ],
  },
  {
    id: 'tier-booster',
    name: 'Booster',
    icon: 'BoltIcon',
    colour: 'border-accent ring-2 ring-accent/20 bg-card',
    iconBg: 'bg-accent/10',
    iconColour: 'text-accent',
    badgeType: 'premium' as const,
    headline: 'Premium Verified — the highest trust mark',
    description:
      'Booster members receive our exclusive Premium Verified badge — a gold-tier trust mark that signals maximum credibility. Combined with priority placement and featured listings, Premium Verified members stand out from the crowd.',
    perks: [
      'Premium Verified badge (gold tier)',
      'Highest-trust mark on the platform',
      'ID, business & farm assurance verified',
      'Featured on homepage with badge',
      'Priority placement in all searches',
      'Dedicated account manager support',
      'Verified status on haulage bids',
    ],
  },
];

const trustStats = [
  { id: 'stat-1', value: '100%', label: 'of members eligible for verification', icon: 'CheckBadgeIcon' },
  { id: 'stat-2', value: '2–3', label: 'business days to verify', icon: 'ClockIcon' },
  { id: 'stat-3', value: '100%', label: 'sellers ID checked', icon: 'ShieldCheckIcon' },
  { id: 'stat-4', value: '0', label: 'extra cost to get verified', icon: 'CurrencyPoundIcon' },
];

const buyerBenefits = [
  {
    id: 'bb-1',
    icon: 'ShieldCheckIcon',
    title: 'Trade with Confidence',
    description:
      'Every seller displaying a Verified badge has had their identity and business details independently confirmed. You know exactly who you are dealing with before you make an enquiry.',
  },
  {
    id: 'bb-2',
    icon: 'MapPinIcon',
    title: 'Traceable to a Real Holding',
    description:
      'Verification means every verified seller is traceable to a real, named agricultural business. That matters for biosecurity, movement records, and peace of mind.',
  },
  {
    id: 'bb-3',
    icon: 'DocumentCheckIcon',
    title: 'Farm Assurance Visibility',
    description:
      'Where a seller holds a farm assurance certificate, this is displayed on their profile. Buyers can filter for Red Tractor, QMS, and other schemes at a glance.',
  },
  {
    id: 'bb-4',
    icon: 'BellAlertIcon',
    title: 'Fraud Prevention Built In',
    description:
      'Our verification process makes it significantly harder for fraudulent listings to appear on the platform. Unverified accounts are clearly labelled so you can make informed decisions.',
  },
];

const sellerBenefits = [
  {
    id: 'sb-1',
    icon: 'EyeIcon',
    title: 'Stand Out to Buyers',
    description:
      'Verified listings receive more enquiries. Buyers actively filter for verified sellers, especially for high-value livestock and machinery purchases.',
  },
  {
    id: 'sb-2',
    icon: 'ArrowTrendingUpIcon',
    title: 'Higher Conversion Rates',
    description:
      'Verified members consistently see higher enquiry-to-sale conversion rates. Trust is the single biggest factor in agricultural trading decisions.',
  },
  {
    id: 'sb-3',
    icon: 'UserGroupIcon',
    title: 'Join a Trusted Community',
    description:
      'Being verified signals that you are a legitimate, professional operator. It builds your reputation on the platform and across the wider farming community.',
  },
  {
    id: 'sb-4',
    icon: 'LockClosedIcon',
    title: 'Protect Your Reputation',
    description:
      'Verification protects you too. It prevents others from impersonating your holding and ensures your listings are clearly distinguished from unverified accounts.',
  },
];

const faqs = [
  {
    id: 'faq-1',
    q: 'Is verification free for all members?',
    a: 'Yes — absolutely. Verification is available to every FarmTradeDirect member regardless of their subscription tier. Free, Pro, and Booster members can all apply at no additional cost. This is our gold standard: trust should not be a premium feature.',
  },
  {
    id: 'faq-2',
    q: 'Do I need a CPH number to sell livestock here?',
    a: 'You do not need to give us one at the moment. If you keep livestock you are legally required to hold a County Parish Holding (CPH) number and to keep movement records, and that obligation is yours either way — but we are not collecting or checking CPH numbers at this stage. We verify your identity and business details instead. We may add CPH checks later, and we will tell you before we do.',
  },
  {
    id: 'faq-3',
    q: 'How long does verification take?',
    a: 'Our UK-based team typically completes verification within 2–3 business days of receiving all required documents. You will receive an email confirmation once your badge has been applied. During busy periods (e.g. spring sales season) it may take up to 5 business days.',
  },
  {
    id: 'faq-4',
    q: 'What is the difference between Verified and Premium Verified?',
    a: 'Both badges confirm that the member has passed our full verification process. The Premium Verified badge (gold tier, exclusive to Booster members) additionally signals that the member is on our highest subscription tier with maximum platform features. Both carry the same level of identity and business verification.',
  },
  {
    id: 'faq-5',
    q: 'Is my personal data secure during verification?',
    a: 'All documents submitted for verification are encrypted in transit and at rest. We use bank-grade TLS encryption and store documents in a secure, access-controlled environment. Documents are retained only for as long as necessary to complete verification and are never shared with third parties.',
  },
  {
    id: 'faq-6',
    q: 'What if my verification is declined?',
    a: 'If we are unable to verify your details, we will contact you by email explaining why and what additional information may be needed. Common reasons include unclear document images or a business name that does not match the name provided. You can resubmit at any time.',
  },
  {
    id: 'faq-7',
    q: 'Can dealers and merchants get verified?',
    a: 'Yes. Livestock dealers, agricultural merchants, and machinery dealers can all be verified. Provide your Companies House registration number and a valid photo ID and we will confirm your business registration and identity through official records.',
  },
  {
    id: 'faq-8',
    q: 'Does verification expire?',
    a: 'Verification does not expire automatically, but we may request updated documents periodically (typically every 2 years) to ensure records remain current. You will be notified well in advance if a re-verification is required.',
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function VerifiedScreen() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <main className="min-h-screen">

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="hero-gradient text-white relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/5 pointer-events-none" />

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-16 md:py-24 relative z-10">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white text-sm font-bold px-4 py-1.5 rounded-full mb-6">
              <Icon name="CheckBadgeIcon" size={16} variant="solid" />
              The FarmTradeDirect Gold Standard
            </div>

            <h1 className="text-4xl md:text-5xl xl:text-6xl font-extrabold leading-tight mb-6">
              Every Member.
              <br />
              <span className="text-amber-300">Every Level.</span>
              <br />
              Verified.
            </h1>

            <p className="text-lg md:text-xl text-green-100 leading-relaxed mb-8 max-w-2xl">
              At FarmTradeDirect, verification is not a premium add-on — it is our baseline. Whether you are on our Free, Pro, or Booster plan, every member has access to full identity and business verification at no extra cost.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                href="/sign-up-login-screen"
                className="btn-accent text-base px-6 py-3"
              >
                <Icon name="CheckBadgeIcon" size={18} />
                Get Verified Now
              </Link>
              <Link
                href="/membership-pricing"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-base px-6 py-3 rounded-md transition-all duration-150"
              >
                <Icon name="StarIcon" size={18} />
                View Membership Plans
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust Stats Bar ──────────────────────────────────────────────── */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustStats.map((stat) => (
              <div key={stat.id} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                  <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} />
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-amber-300 tabular-nums">{stat.value}</div>
                  <div className="text-xs text-green-200 leading-tight">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Ad Banner ────────────────────────────────────────────────────── */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pt-8">
        <div className="flex justify-center">
          <AdBanner width={728} height={90} label="728×90 Verified Page Banner" className="rounded-lg" />
        </div>
      </div>

      {/* ── What Verification Means ──────────────────────────────────────── */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: text */}
          <div>
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full mb-5">
              <Icon name="ShieldCheckIcon" size={15} variant="solid" />
              Why Verification Matters
            </div>
            <h2 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-5 leading-tight">
              Trust is the foundation of every agricultural trade
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-4">
              Agricultural trading involves significant sums of money, live animals, and complex biosecurity obligations. Knowing who you are dealing with is not optional — it is essential.
            </p>
            <p className="text-muted-foreground text-base leading-relaxed mb-4">
              FarmTradeDirect's verification process confirms that every verified member is a real, traceable person or business with a legitimate connection to UK agriculture. We validate photo ID, confirm farm and business details, and check farm assurance status where applicable.
            </p>
            <p className="text-muted-foreground text-base leading-relaxed mb-6">
              Crucially, we do not reserve this for paying subscribers. Our gold standard is simple: <strong className="text-foreground">every member, at every level, can be verified</strong>.
            </p>
            <div className="flex flex-col gap-3">
              {[
                'Farm and business details confirmed',
                'Photo ID verified against the account holder',
                'Farm assurance certificates confirmed',
                'Business registrations validated for dealers',
                'UK-based verification team',
              ].map((point, i) => (
                <div key={`point-${i}`} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/15 flex items-center justify-center shrink-0">
                    <Icon name="CheckIcon" size={12} className="text-success" />
                  </div>
                  <span className="text-sm text-foreground font-medium">{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: visual card */}
          <div className="relative">
            <div className="bg-card border border-border rounded-2xl shadow-card p-8">
              {/* Mock profile card */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="UserCircleIcon" size={40} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-extrabold text-foreground text-lg">James Hartley</span>
                    <VerifiedBadge type="standard" size="md" />
                  </div>
                  <p className="text-sm text-muted-foreground">Hartley Livestock, North Yorkshire</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Skipton, North Yorkshire · Red Tractor Assured</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { label: 'Identity', status: 'Verified', icon: 'IdentificationIcon', colour: 'text-success bg-success/10' },
                  { label: 'Business Details', status: 'Confirmed', icon: 'DocumentCheckIcon', colour: 'text-success bg-success/10' },
                  { label: 'Farm Assurance', status: 'Red Tractor', icon: 'ShieldCheckIcon', colour: 'text-success bg-success/10' },
                  { label: 'Member Since', status: '2024', icon: 'CalendarIcon', colour: 'text-primary bg-primary/10' },
                ].map((item, i) => (
                  <div key={`card-item-${i}`} className="bg-muted rounded-lg p-3">
                    <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded-full mb-1.5 ${item.colour}`}>
                      <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={11} />
                      {item.status}
                    </div>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-xs text-muted-foreground text-center">
                  This member has been independently verified by FarmTradeDirect
                </p>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
              <Icon name="CheckBadgeIcon" size={14} variant="solid" />
              ID Confirmed
            </div>
          </div>
        </div>
      </section>

      {/* ── Verification at Every Level ──────────────────────────────────── */}
      <section className="bg-secondary/40 py-14">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full mb-4">
              <Icon name="CheckBadgeIcon" size={15} variant="solid" />
              Verification at Every Level
            </div>
            <h2 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-4">
              No paywalls on trust
            </h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Unlike other platforms that reserve verification for premium subscribers, FarmTradeDirect makes verification available to every member — because trust should never be a paid feature.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {membershipTiers.map((tier) => (
              <div
                key={tier.id}
                className={`rounded-2xl border-2 p-6 shadow-card listing-card-hover ${tier.colour}`}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tier.iconBg}`}>
                    <Icon name={tier.icon as Parameters<typeof Icon>[0]['name']} size={20} className={tier.iconColour} variant="solid" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-foreground text-lg">{tier.name}</span>
                      <VerifiedBadge type={tier.badgeType} size="sm" />
                    </div>
                    <p className="text-xs text-muted-foreground">Membership tier</p>
                  </div>
                </div>

                <h3 className="font-bold text-foreground text-base mb-2">{tier.headline}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{tier.description}</p>

                <ul className="flex flex-col gap-2">
                  {tier.perks.map((perk, pi) => (
                    <li key={`perk-${tier.id}-${pi}`} className="flex items-start gap-2.5">
                      <Icon name="CheckCircleIcon" size={15} className="text-success shrink-0 mt-0.5" variant="solid" />
                      <span className="text-sm text-foreground">{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────── */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-14">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full mb-4">
            <Icon name="ArrowPathIcon" size={15} />
            The Verification Process
          </div>
          <h2 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-4">
            How verification works
          </h2>
          <p className="text-muted-foreground text-base max-w-xl mx-auto">
            A straightforward four-step process completed entirely online. Most members are verified within 2–3 business days.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {verificationSteps.map((step, idx) => (
            <div key={step.id} className="relative">
              {/* Connector line (desktop) */}
              {idx < verificationSteps.length - 1 && (
                <div className="hidden xl:block absolute top-8 left-[calc(100%-1rem)] w-8 h-0.5 bg-border z-10" />
              )}

              <div className="bg-card border border-border rounded-2xl p-6 shadow-card listing-card-hover h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-primary" />
                  </div>
                  <span className="text-3xl font-extrabold text-muted-foreground/30 tabular-nums">{step.step}</span>
                </div>
                <h3 className="font-bold text-foreground text-base mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Documents Required ───────────────────────────────────────────── */}
      <section className="bg-muted/60 py-14">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full mb-4">
              <Icon name="DocumentTextIcon" size={15} />
              What You Need
            </div>
            <h2 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-4">
              Documents required for verification
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              We keep the process simple. Most farmers need just two documents to get fully verified.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {documentsRequired.map((doc) => (
              <div key={doc.id} className="bg-card border border-border rounded-2xl p-6 shadow-card listing-card-hover">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon name={doc.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="font-bold text-foreground text-base">{doc.title}</h3>
                      {doc.required ? (
                        <span className="text-xs font-bold bg-danger/10 text-danger px-2 py-0.5 rounded-full">Required</span>
                      ) : (
                        <span className="text-xs font-bold bg-muted text-muted-foreground px-2 py-0.5 rounded-full">If applicable</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{doc.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            All documents are encrypted and handled in accordance with UK GDPR. We never share your documents with third parties.
          </p>
        </div>
      </section>

      {/* ── Benefits Split ───────────────────────────────────────────────── */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-14">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full mb-4">
            <Icon name="SparklesIcon" size={15} variant="solid" />
            Benefits of Verification
          </div>
          <h2 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-4">
            Better for buyers. Better for sellers.
          </h2>
          <p className="text-muted-foreground text-base max-w-xl mx-auto">
            Verification creates a safer, more efficient marketplace for everyone on the platform.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Buyers */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-info/10 flex items-center justify-center">
                <Icon name="ShoppingCartIcon" size={20} className="text-info" />
              </div>
              <h3 className="text-xl font-extrabold text-foreground">For Buyers</h3>
            </div>
            <div className="flex flex-col gap-4">
              {buyerBenefits.map((benefit) => (
                <div key={benefit.id} className="flex items-start gap-4 bg-card border border-border rounded-xl p-4 shadow-card listing-card-hover">
                  <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
                    <Icon name={benefit.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-info" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground text-sm mb-1">{benefit.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sellers */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="TagIcon" size={20} className="text-success" />
              </div>
              <h3 className="text-xl font-extrabold text-foreground">For Sellers</h3>
            </div>
            <div className="flex flex-col gap-4">
              {sellerBenefits.map((benefit) => (
                <div key={benefit.id} className="flex items-start gap-4 bg-card border border-border rounded-xl p-4 shadow-card listing-card-hover">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                    <Icon name={benefit.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-success" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground text-sm mb-1">{benefit.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="bg-secondary/40 py-14">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full mb-4">
              <Icon name="QuestionMarkCircleIcon" size={15} />
              Frequently Asked Questions
            </div>
            <h2 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-4">
              Everything you need to know
            </h2>
          </div>

          <div className="max-w-3xl mx-auto flex flex-col gap-3">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-card border border-border rounded-xl overflow-hidden shadow-card"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-muted/40 transition-colors duration-150"
                  aria-expanded={openFaq === faq.id}
                >
                  <span className="font-semibold text-foreground text-sm">{faq.q}</span>
                  <Icon
                    name={openFaq === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={18}
                    className="text-muted-foreground shrink-0 transition-transform duration-200"
                  />
                </button>
                {openFaq === faq.id && (
                  <div className="px-6 pb-5 border-t border-border">
                    <p className="text-sm text-muted-foreground leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="hero-gradient text-white py-16">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckBadgeIcon" size={32} variant="solid" className="text-amber-300" />
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
              Ready to get your Verified badge?
            </h2>
            <p className="text-green-100 text-lg leading-relaxed mb-8">
              Join thousands of verified farmers, dealers, and hauliers on FarmTradeDirect. Create your account today — verification is free at every membership level.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/sign-up-login-screen"
                className="btn-accent text-base px-8 py-3.5"
              >
                <Icon name="UserPlusIcon" size={18} />
                Create Free Account
              </Link>
              <Link
                href="/membership-pricing"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-base px-8 py-3.5 rounded-md transition-all duration-150"
              >
                <Icon name="StarIcon" size={18} />
                Compare Membership Plans
              </Link>
            </div>
            <p className="text-green-200 text-sm mt-6">
              No credit card required · Free tier available · Verification at every level
            </p>
          </div>
        </div>
      </section>

    </main>
  );
}
